import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);

export const shortRelativeTime = (date: string | Date | number): string => {
  const now = dayjs();
  const target = dayjs(date);
  const diff = now.diff(target);

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return `${years} yr`;
  if (months > 0) return `${months} mo`;
  if (days > 0) return `${days} days`;
  if (hours > 0) return `${hours} hr`;
  if (minutes > 0) return `${minutes} min`;
  return "just now";
};

export { dayjs };

export const toUTCString = (date: string, format = "MM/DD/YYYY") => {
  return dayjs.utc(date).format(format);
};

export const toUTCDate = (date: Date | string | undefined) => {
  if (!date) return undefined;
  return new Date(dayjs(date).utc().format("YYYY-MM-DD") + "T00:00:00");
};

/**
 * Formats a date-only value (like dateOfBirth or dateOfLoss) without timezone conversion.
 * This function treats the input as a pure date (YYYY-MM-DD) and formats it directly
 * without interpreting it as a timestamp in any timezone.
 *
 * @param date - Date string in YYYY-MM-DD format or ISO format
 * @param format - Output format (default: MM/DD/YYYY)
 * @returns Formatted date string
 */
export const formatDateOnly = (
  date: string | undefined | null,
  format = "MM/DD/YYYY"
): string => {
  if (!date) return "-";

  const dateOnly = date.split("T")[0];

  const [year, month, day] = dateOnly.split("-").map(Number);

  const localDate = new Date(year, month - 1, day, 12, 0, 0);

  return dayjs(localDate).format(format);
};

const PAGES_PER_MINUTE = 25 as const;

export const formatProcessingEstimate = (
  totalPages: number,
  locale: string
): string => {
  const totalSeconds = Math.max(
    1,
    Math.ceil((totalPages / PAGES_PER_MINUTE) * 60)
  );
  const d = dayjs.duration(totalSeconds, "seconds");

  const formatUnit = (
    unit: "hour" | "minute" | "second",
    value: number
  ): string => {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      unitDisplay: "narrow",
    }).format(value);
  };

  const hours = Math.floor(d.asHours());
  const minutes = Math.ceil(d.asMinutes() % 60);
  const seconds = Math.max(1, Math.ceil(d.asSeconds() % 60));

  if (hours > 0) {
    return minutes > 0
      ? `~${formatUnit("hour", hours)} ${formatUnit("minute", minutes)}`
      : `~${formatUnit("hour", hours)}`;
  }

  if (d.asMinutes() >= 1) {
    return `~${formatUnit("minute", Math.ceil(d.asMinutes()))}`;
  }

  return `~${formatUnit("second", seconds)}`;
};
